import { useEffect, useState, type ReactNode } from "react";
import type { ToastActionElement, ToastProps } from "../components/ui/toast";

const TOAST_LIMIT = 1;

const TOAST_CONFIG = {
    default: { delay: Infinity },
    destructive: { delay: Infinity },
    success: { delay: 5000 },
    warning: { delay: 5000 },
    info: { delay: 5000 },
} as const;

type ToasterToast = ToastProps & {
    id: string;
    title?: ReactNode;
    description?: ReactNode;
    action?: ToastActionElement;
};

type Action =
    | {
          type: "ADD_TOAST";
          toast: ToasterToast;
      }
    | {
          type: "UPDATE_TOAST";
          toast: Partial<ToasterToast>;
      }
    | {
          type: "DISMISS_TOAST";
          toastId?: ToasterToast["id"];
      }
    | {
          type: "REMOVE_TOAST";
          toastId?: ToasterToast["id"];
      };

interface State {
    toasts: ToasterToast[];
}

export const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "ADD_TOAST":
            return {
                ...state,
                toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
            };

        case "UPDATE_TOAST":
            return {
                ...state,
                toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
            };

        case "DISMISS_TOAST": {
            const { toastId } = action;

            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    t.id === toastId || toastId === undefined
                        ? {
                              ...t,
                              open: false,
                          }
                        : t
                ),
            };
        }
        case "REMOVE_TOAST":
            if (action.toastId === undefined) {
                return {
                    ...state,
                    toasts: [],
                };
            }
            return {
                ...state,
                toasts: state.toasts.filter((t) => t.id !== action.toastId),
            };
    }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
    memoryState = reducer(memoryState, action);
    listeners.forEach((listener) => {
        listener(memoryState);
    });
}

type Toast = Omit<ToasterToast, "id">;

function toast({ ...props }: Toast) {
    const id = Math.random().toString(36).substring(2, 11);
    const variant = props.variant || "default";
    const config = TOAST_CONFIG[variant as keyof typeof TOAST_CONFIG];

    const update = (props: ToasterToast) =>
        dispatch({
            type: "UPDATE_TOAST",
            toast: { ...props, id },
        });

    const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

    // Create the toast
    dispatch({
        type: "ADD_TOAST",
        toast: {
            ...props,
            id,
            open: true,
            duration: config.delay, // Control Radix auto-dismiss
            onOpenChange: (open: boolean) => {
                if (!open) {
                    // When animation completes, remove from array
                    setTimeout(() => {
                        dispatch({ type: "REMOVE_TOAST", toastId: id });
                    }, 100);
                }
            },
        },
    });

    return {
        id,
        dismiss,
        update,
    };
}

function useToast() {
    const [state, setState] = useState<State>(memoryState);

    useEffect(() => {
        listeners.push(setState);
        return () => {
            const index = listeners.indexOf(setState);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        };
    }, [state]);

    return {
        ...state,
        toast,
        dismiss: (toastId?: string) => {
            dispatch({ type: "DISMISS_TOAST", toastId });
        },
    };
}

export { toast, useToast };
